import { BookingTypeIcon } from 'common-components/tags';
import TextTag from 'common-components/tags/TextTag';
import { Text } from 'common-components/typography';
import moment from 'moment';
import React from 'react';
import { BookingType, ShiftSlotStatus } from 'utilities/enum-utils';
import { Broadcast } from '@good/icons';
import { IRosterShift } from '../../types';
import { getTimelineCardWidth, getTimelineOffset } from './card-width-utils';
import { ROSTER_DAILY_CONFIG } from '../roster-daily-config';
import { Paper, UnstyledButton } from '@good/ui/core';
import { useBookings } from 'views/bookings-v2/books-view-context/bookings-view-context';
import { ROSTER_TAB } from '../../components/roster-common-enums';

const { TIMELINE_CARD_HEIGHT, TIMELINE_ROW_HEIGHT } = ROSTER_DAILY_CONFIG;

interface OpenShiftCardProps {
  shift: IRosterShift;
  rowIndex: number;
}

export function OpenShiftCard({ shift, rowIndex }: OpenShiftCardProps) {
  const { shiftStartDateTime, shiftEndDateTime, serviceName, duplicateCount } = shift;
  const mStartDate = moment(shiftStartDateTime);
  const mEndDate = moment(shiftEndDateTime);
  const startTimeDisplay = mStartDate.format('h:mm a');
  const endTimeDisplay = mEndDate.format('h:mm a');
  const cardWidth = getTimelineCardWidth(shiftStartDateTime, shiftEndDateTime);
  const leftOffset = getTimelineOffset(shiftStartDateTime);
  const topOffset = rowIndex === 0 ? 4 : rowIndex * TIMELINE_ROW_HEIGHT + 4;
  const onSelectRosterItem = useBookings((selector) => selector.onSelectRosterItem);
  const selectedRosterItem = useBookings((selector) => selector.selectedRosterItem);
  const isSelected = selectedRosterItem?.bookingId === shift.bookingId;

  return (
    <>
      <UnstyledButton
        onClick={() => onSelectRosterItem({ item: shift, type: 'openSlot', tab: ROSTER_TAB.DAILY })}
        display='flex'
        w={cardWidth}
        top={topOffset}
        left={leftOffset}
        h={TIMELINE_CARD_HEIGHT}
        pos='absolute'
        style={{
          zIndex: 10,
          boxShadow: isSelected ? '0 0 0 2px var(--mantine-color-orange-6)' : '',
          borderRadius: 'calc(var(--mantine-radius-md) - 2px)',
        }}
      >
        <Paper
          p={6}
          w={cardWidth}
          withBorder
          h={TIMELINE_CARD_HEIGHT}
          shadow={isSelected ? 'xl' : 'xs'}
          bg='yellow.0'
          style={{
            borderColor: 'var(--mantine-color-yellow-6)',
            overflow: 'hidden',
          }}
          className='anim-fade-in border-dashed'
        >
          <div className='flex-row align-center'>
            {(duplicateCount ?? 0) > 1 && (
              <TextTag content={`${duplicateCount}x`} color={'gold'} size={'small'} className='mr-x-small' />
            )}

            {shift.shiftSlotStatus === ShiftSlotStatus.PUBLISHING ||
            shift.shiftSlotStatus === ShiftSlotStatus.PUBLISHED ? (
              <>
                <span className='mr-x2-small' style={{ fontSize: '10px', lineHeight: '1' }}>
                  <Broadcast />
                </span>
                <TextTag
                  content={'Published'}
                  theme='light'
                  color='rose'
                  size='small'
                  weight='bold'
                  backgroundColor={'white'}
                />
              </>
            ) : (
              <TextTag
                content={'Open slot'}
                theme='light'
                color='gold'
                size='small'
                weight='bold'
                backgroundColor={'white'}
              />
            )}
          </div>
          <div className='pt-x-small flex-column line-height-100'>
            <div className='text-overflow-ellipsis overflow-hidden whitespace-nowrap'>
              <Text lineHeight={100} size='small' className='whitespace-nowrap'>
                <b>{startTimeDisplay}</b> - {endTimeDisplay}
              </Text>
            </div>
            <div className={'flex-row align-center'}>
              <BookingTypeIcon
                type={
                  shift.bookingType === BookingType.ACTIVITY_RECORD ? BookingType.ACTIVITY_RECORD : shift.serviceType
                }
                className={'mr-x-small'}
              />
              <div className='text-overflow-ellipsis overflow-hidden whitespace-nowrap'>
                <Text lineHeight={100} size='regular' className='whitespace-nowrap'>
                  {serviceName}
                </Text>
              </div>
            </div>
          </div>
        </Paper>
      </UnstyledButton>
    </>
  );
}

export default OpenShiftCard;
